import type { PdfCacheProps } from '../context/PdfCacheContext';
import type { GeneratePdfError, GeneratePdfRequest, GeneratePdfSuccess } from './pdfWorker';

class PdfWorkerManager {
  private worker: Worker | null = null;
  private pendingRequests = new Map<
    string,
    {
      resolve: (url: string) => void;
      reject: (error: Error) => void;
    }
  >();

  constructor() {
    this.initWorker();
  }

  private initWorker() {
    try {
      // Create worker from the worker file using Vite's worker syntax
      this.worker = new Worker(new URL('./pdfWorker.ts', import.meta.url), {
        type: 'module',
        name: 'pdf-worker',
      });

      this.worker.onmessage = this.handleWorkerMessage.bind(this);
      this.worker.onerror = this.handleWorkerError.bind(this);

      console.log('PdfWorkerManager: Worker initialized');
    } catch (error) {
      console.error('PdfWorkerManager: Failed to initialize worker:', error);
    }
  }

  private handleWorkerMessage(event: MessageEvent<GeneratePdfSuccess | GeneratePdfError>) {
    const { type, id, data } = event.data;

    const pending = this.pendingRequests.get(id);
    if (!pending) {
      console.warn('PdfWorkerManager: Received response for unknown request:', id);
      return;
    }

    this.pendingRequests.delete(id);

    if (type === 'PDF_GENERATED') {
      const { url } = data as GeneratePdfSuccess['data'];
      pending.resolve(url);
    } else if (type === 'PDF_ERROR') {
      const { error } = data as GeneratePdfError['data'];
      pending.reject(new Error(error));
    }
  }

  private handleWorkerError(error: ErrorEvent) {
    console.error('PdfWorkerManager: Worker error:', error);

    // Reject all pending requests
    this.pendingRequests.forEach(({ reject }) => {
      reject(new Error('Worker error: ' + error.message));
    });
    this.pendingRequests.clear();
  }

  public async generatePdf(props: PdfCacheProps): Promise<string> {
    if (!this.worker) {
      throw new Error('PDF Worker not available - falling back to main thread');
    }

    return new Promise((resolve, reject) => {
      const id = `pdf_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

      // Store the promise callbacks
      this.pendingRequests.set(id, { resolve, reject });

      // Send request to worker
      const request: GeneratePdfRequest = {
        type: 'GENERATE_PDF',
        id,
        data: props,
      };

      this.worker!.postMessage(request);

      // Set a timeout for the request
      setTimeout(() => {
        if (this.pendingRequests.has(id)) {
          this.pendingRequests.delete(id);
          reject(new Error('PDF generation timeout'));
        }
      }, 60000); // 60 second timeout
    });
  }

  public isWorkerAvailable(): boolean {
    return this.worker !== null;
  }

  public terminate() {
    if (this.worker) {
      this.worker.terminate();
      this.worker = null;
    }

    // Reject all pending requests
    this.pendingRequests.forEach(({ reject }) => {
      reject(new Error('Worker terminated'));
    });
    this.pendingRequests.clear();
  }
}

// Singleton instance
export const pdfWorkerManager = new PdfWorkerManager();
